import React, { useState, useEffect, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  Stack,
  Paper,
  Container,
  Fade
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { supabase } from '../utils/supabaseClient';
import { useAuth } from '../AuthProvider';
import { updateUserProfile } from '../API';
import { themeColors } from '../styles/theme';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import suntapLogo from '../img/suntap_logo.svg';

// Styled components matching the app's design patterns
const StyledButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 500,
  borderRadius: '48px',
  padding: '12px 20px',
  fontSize: '1rem',
  fontFamily: "'DM Sans', sans-serif",
  transition: 'all 0.2s ease',
}));

const PrimaryButton = styled(StyledButton)(({ theme }) => ({
  background: themeColors.orange,
  color: '#FFF',
  border: `2px solid ${themeColors.orange}`,
  '&:hover': {
    background: '#E65100',
    borderColor: '#E65100',
  },
}));

const WelcomeBox = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 16,
  background: `linear-gradient(135deg, ${themeColors.lightBlue}15 0%, ${themeColors.lightYellow}15 100%)`,
  marginBottom: theme.spacing(3),
  border: `2px solid ${themeColors.orange}20`,
}));

export default function WelcomePage() {
  const { profile, refreshProfile, user, session, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [step, setStep] = useState<'welcome' | 'setup'>('welcome');
  const [isInitializing, setIsInitializing] = useState(true);

  // Check if user is already onboarded and redirect
  useEffect(() => {
    // Wait for auth to load
    if (authLoading) return;
    
    // If user is already onboarded, redirect immediately
    if (session?.user?.user_metadata?.onboarded) {
      navigate('/', { replace: true });
    } else {
      // Only show the page if user is not onboarded
      setIsInitializing(false);
    }
  }, [session, navigate, authLoading]);

  // Pre-fill display name for Google users
  useEffect(() => {
    const provider = session?.user?.app_metadata?.provider;
    const isGoogleUser = provider === 'google';
    const googleDisplayName = session?.user?.user_metadata?.full_name;
    
    if (isGoogleUser && googleDisplayName) {
      setDisplayName(googleDisplayName);
    } else if (profile?.display_name && profile.display_name !== user?.id) {
      // Don't pre-fill if it's just the user ID/hash
      setDisplayName(profile.display_name);
    }
  }, [profile, user, session]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    
    // Validate display name
    const trimmedName = displayName.trim();
    if (!trimmedName) {
      setError('Please enter a display name');
      return;
    }
    
    if (trimmedName.length < 2) {
      setError('Display name must be at least 2 characters long');
      return;
    }
    
    if (trimmedName.length > 50) {
      setError('Display name must be less than 50 characters');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Update the profile
      const response = await updateUserProfile(trimmedName, true);
      const data = response.data;

      if (response.status === 200 && data.status === 'success') {
        // Set the onboarded flag in user metadata
        await supabase.auth.updateUser({ data: { onboarded: true } });
        
        await refreshProfile();
        
        // Navigate to home page
        navigate('/', { replace: true });
      } else {
        setError(data.message || data.error || 'Failed to update profile.');
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = async () => {
    try {
      setIsLoading(true);
      
      // If skipping, set a default display name if none exists
      if (!displayName.trim()) {
        const provider = session?.user?.app_metadata?.provider;
        const isGoogleUser = provider === 'google';
        const googleDisplayName = session?.user?.user_metadata?.full_name;
        const defaultName = (isGoogleUser && googleDisplayName) ? googleDisplayName : `User${user?.id?.substring(0, 8)}`;
        
        await updateUserProfile(defaultName, true);
      }
      
      // Set the onboarded flag
      await supabase.auth.updateUser({ data: { onboarded: true } });
      
      await refreshProfile();
      navigate('/', { replace: true });
    } catch (err) {
      console.error('Error during skip:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading while checking auth/onboarding status
  if (authLoading || isInitializing) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        py: 4
      }}>
        {step === 'welcome' ? (
          <Fade in={true}>
            <Box sx={{ width: '100%', textAlign: 'center' }}>
              <img 
                src="/circular_logo_icon.png" 
                alt="Suntap Logo" 
                style={{ 
                  width: 180, 
                  height: 180, 
                  marginBottom: '24px',
                  display: 'block',
                  margin: '0 auto 24px auto'
                }} 
              />
              <img
                src={suntapLogo}
                alt="Suntap Text Logo"
                style={{
                  height: '40px',
                  marginBottom: '64px',
                  display: 'block',
                  margin: '0 auto 64px auto'
                }}
              />
              
              <WelcomeBox elevation={0}>
                <Stack spacing={2}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: themeColors.darkBackground }}>
                    🌞 You're now part of our growing community! 🌞
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    With your account, you can:
                  </Typography>
                  <Stack spacing={1} sx={{ pl: 2, textAlign: 'left' }}>
                    <Typography variant="body2">
                      • Submit and edit pub gardens
                    </Typography>
                    <Typography variant="body2">
                      • Earn points for your contributions
                    </Typography>
                    <Typography variant="body2">
                      • Compete on community leaderboards
                    </Typography>
                    <Typography variant="body2">
                      • Track your submission history
                    </Typography>
                  </Stack>
                </Stack>
              </WelcomeBox>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                Let's finish setting up your profile.
              </Typography>
              
              <PrimaryButton
                onClick={() => setStep('setup')}
                startIcon={<PersonAddIcon />}
                size="large"
                sx={{ minWidth: 200 }}
              >
                Get started
              </PrimaryButton>
            </Box>
          </Fade>
        ) : (
          <Fade in={true}>
            <Box sx={{ width: '100%' }}>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <AutoAwesomeIcon sx={{ fontSize: 48, color: themeColors.orange, mb: 2 }} />
                <Typography variant="h4" component="h1" sx={{ 
                  fontWeight: 600, 
                  fontFamily: "'DM Sans', sans-serif",
                  mb: 1 
                }}>
                  Set Up Your Profile
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Choose how you'd like to be known in the community
                </Typography>
              </Box>

              <Paper sx={{ p: 4, borderRadius: 3 }}>
                <form onSubmit={handleSubmit}>
                  <Stack spacing={3}>
                    <TextField
                      label="Display Name"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      fullWidth
                      required
                      disabled={isLoading}
                      helperText="This is how other users will see you"
                      inputProps={{ maxLength: 50 }}
                    />
                    
                    {error && (
                      <Alert severity="error" sx={{ borderRadius: 2 }}>
                        {error}
                      </Alert>
                    )}
                    
                    <Stack direction="row" spacing={2} sx={{ pt: 2 }}>
                      <Button
                        onClick={handleSkip}
                        disabled={isLoading}
                        sx={{ flex: 1 }}
                      >
                        Skip for now
                      </Button>
                      <PrimaryButton
                        type="submit"
                        disabled={isLoading}
                        startIcon={isLoading ? <CircularProgress size={20} /> : <PersonAddIcon />}
                        sx={{ flex: 1 }}
                      >
                        {isLoading ? 'Setting up...' : 'Complete Setup'}
                      </PrimaryButton>
                    </Stack>
                  </Stack>
                </form>
              </Paper>
            </Box>
          </Fade>
        )}
      </Box>
    </Container>
  );
} 